import React, { useState, useEffect, memo } from 'react';
import { Chip, MenuItem, Popover } from '@mui/material';
import API from '../services/api';

const ChipFormat = memo(function ChipFormat({
  value,
  itemKey,
  onFormatChange,
  chipColor = 'primary',
  size = 'small'
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  // inicia como string vacío para evitar toLowerCase sobre objetos en el primer render
  const [localValue, setLocalValue] = useState('');
  const [formats, setFormats] = useState([]);

  useEffect(() => {
    // value can be string (name), object {id,name} or id
    if (!value) { setLocalValue(''); return; }
    if (typeof value === 'string') { setLocalValue(value); return; }
    if (typeof value === 'number') {
      const f = formats.find(x => String(x.id) === String(value));
      setLocalValue(f ? f.name : '');
      return;
    }
    if (typeof value === 'object') {
      setLocalValue(value.name || '');
    }
  }, [value, formats]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await API.get('catalogs/create_order/');
        const list = Array.isArray(data?.formats) ? data.formats : [];
        if (mounted) setFormats(list);
      } catch { if (mounted) setFormats([]); }
    })();
    return () => { mounted = false; };
  }, []);

  const handleChipClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFormatSelect = (fmt) => {
    const label = (fmt && fmt.name) || '';
    if (label !== localValue) {
      setLocalValue(label);
      // notify parent with the full object
      onFormatChange(itemKey, fmt);
    }
    handleClose();
  };

  const displayLabel = React.useMemo(() => {
    if (!localValue) return 'formato';
    if (typeof localValue === 'string') return localValue.toLowerCase();
    if (typeof localValue === 'object') return String(localValue.name || 'formato').toLowerCase();
    if (typeof localValue === 'number') {
      const f = formats.find(x => String(x.id) === String(localValue));
      return String(f?.name || 'formato').toLowerCase();
    }
    return 'formato';
  }, [localValue, formats]);

  return (
    <>
      <Chip
        label={displayLabel}
        size={size}
        color={chipColor}
        onClick={handleChipClick}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 1,
          textTransform: 'capitalize',
          cursor: 'pointer'
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {formats.map((fmt) => (
          <MenuItem
            key={fmt.id}
            onClick={() => handleFormatSelect(fmt)}
            selected={fmt.name === localValue}
          >
            {fmt.name}
          </MenuItem>
        ))}
      </Popover>
    </>
  );
});

export default ChipFormat;
